import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { ShellSessionCreateResponse, TunnelControlFrame } from '@devrobot/shared';
import { ArrowLeft, PlugZap, RotateCw, ShieldAlert } from 'lucide-react';
import { api, seg } from '../../api/client';
import { useVps } from '../../api/queries';
import { useUser } from '../../api/user';
import { ErrorAlert } from '../../components/ErrorAlert';
import { Terminal, type TerminalHandle } from '../../components/Terminal';
import { Haptic } from '../../kit/haptics';

type Status = 'connecting' | 'connected' | 'ended';

type Ended =
  | { kind: 'closed'; message: string; exitCode: number | null }
  | { kind: 'lost' }
  | { kind: 'disconnected' }
  | { kind: 'failed'; error: unknown };

const STATUS_BADGE: Record<Status, { cls: string; label: string }> = {
  connecting: { cls: 'badge-info', label: 'connecting' },
  connected: { cls: 'badge-success', label: 'connected' },
  ended: { cls: 'badge-neutral', label: 'ended' },
};

function tunnelUrl(wsPath: string, ticket: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}${wsPath}?ticket=${encodeURIComponent(ticket)}`;
}

const encoder = new TextEncoder();

/** Binary frame payload as bytes (realm-independent checks). */
function toBytes(data: unknown): Uint8Array | null {
  if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  if (Object.prototype.toString.call(data) === '[object ArrayBuffer]') return new Uint8Array(data as ArrayBuffer);
  return null;
}

export default function VpsTerminalPage() {
  const { id = '' } = useParams();
  const user = useUser();
  const vps = useVps(id);
  const name = vps.data?.name ?? id;

  const [term, setTerm] = useState<TerminalHandle | null>(null);
  const [status, setStatus] = useState<Status>('connecting');
  const [ended, setEnded] = useState<Ended | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  // Bumped on every (re)connect and on unmount; stale sockets and requests are ignored.
  const genRef = useRef(0);

  const closeSocket = () => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) ws.close(1000);
  };

  const connect = useCallback(
    async (t: TerminalHandle) => {
      const gen = ++genRef.current;
      closeSocket();
      setStatus('connecting');
      setEnded(null);
      const { cols, rows } = t.size();
      let res: ShellSessionCreateResponse;
      try {
        res = await api.post<ShellSessionCreateResponse>(`/api/v1/vps/${seg(id)}/shell-sessions`, { kind: 'pty', cols, rows });
      } catch (error) {
        if (gen !== genRef.current) return;
        setStatus('ended');
        setEnded({ kind: 'failed', error });
        return;
      }
      if (gen !== genRef.current) return;

      let closeFrame: Extract<TunnelControlFrame, { t: 'close' }> | null = null;
      let exitCode: number | null = null;
      const ws = new WebSocket(tunnelUrl(res.wsPath, res.ticket));
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;
      const current = () => gen === genRef.current && wsRef.current === ws;

      ws.onopen = () => {
        if (!current()) return;
        const size = t.size();
        ws.send(JSON.stringify({ t: 'resize', cols: size.cols, rows: size.rows }));
      };
      ws.onmessage = (ev: MessageEvent) => {
        if (!current()) return;
        if (typeof ev.data !== 'string') {
          const bytes = toBytes(ev.data);
          if (bytes) t.write(bytes);
          setStatus((s) => (s === 'connecting' ? 'connected' : s));
          return;
        }
        let frame: TunnelControlFrame;
        try {
          frame = JSON.parse(ev.data) as TunnelControlFrame;
        } catch {
          return;
        }
        switch (frame.t) {
          case 'ready':
            setStatus('connected');
            t.focus();
            break;
          case 'exit':
            exitCode = frame.code;
            break;
          case 'error':
            t.write(`\r\n\x1b[31m${frame.message}\x1b[0m\r\n`);
            break;
          case 'close':
            closeFrame = frame;
            break;
        }
      };
      ws.onclose = () => {
        if (gen !== genRef.current) return;
        if (wsRef.current === ws) wsRef.current = null;
        setStatus('ended');
        const cf = closeFrame as Extract<TunnelControlFrame, { t: 'close' }> | null;
        if (cf) setEnded({ kind: 'closed', message: cf.message, exitCode: cf.exitCode ?? exitCode });
        else setEnded({ kind: 'lost' });
      };
    },
    [id],
  );

  useEffect(() => {
    if (term && user.platformAdmin) void connect(term);
  }, [term, connect, user.platformAdmin]);

  // Close the socket when leaving the page.
  useEffect(
    () => () => {
      genRef.current++;
      closeSocket();
    },
    [],
  );

  const onData = useCallback((data: string) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(encoder.encode(data));
  }, []);

  const onResize = useCallback((cols: number, rows: number) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'resize', cols, rows }));
  }, []);

  const disconnect = () => {
    genRef.current++;
    closeSocket();
    setStatus('ended');
    setEnded({ kind: 'disconnected' });
  };

  const reconnect = () => {
    if (!term) return;
    term.reset();
    void connect(term);
  };

  const badge = STATUS_BADGE[status];

  return (
    <div className="flex h-[calc(100dvh-7.5rem)] min-h-80 flex-col gap-3">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Link to={`/vps/${seg(id)}`} className="btn btn-ghost btn-sm btn-square" aria-label={`Back to ${name}`}>
            <ArrowLeft className="size-4" aria-hidden="true" />
          </Link>
          <h1 className="break-anywhere text-lg font-bold tracking-tight">Root shell on {name}</h1>
          <span className={`badge badge-soft badge-sm gap-1.5 ${badge.cls}`} role="status" aria-live="polite">
            {status === 'connecting' ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : null}
            {status === 'connected' ? (
              <span className="inline-grid *:[grid-area:1/1]" aria-hidden="true">
                <span className="status status-success status-sm animate-ping" />
                <span className="status status-success status-sm" />
              </span>
            ) : null}
            {badge.label}
          </span>
          <span className="badge badge-warning badge-soft badge-sm">
            <ShieldAlert className="size-3" aria-hidden="true" /> Audited session · runs as root
          </span>
        </div>
        {user.platformAdmin ? (
          <div className="flex items-center gap-2">
            <Haptic disabled={status === 'ended'}>
              <button type="button" className="btn btn-sm btn-outline btn-error" onClick={disconnect} disabled={status === 'ended'}>
                <PlugZap className="size-4" aria-hidden="true" /> Disconnect
              </button>
            </Haptic>
          </div>
        ) : null}
      </header>

      {!user.platformAdmin ? (
        <div role="alert" className="alert alert-warning alert-soft">
          Only platform administrators can open a shell on a VPS.
        </div>
      ) : (
        <div className="relative min-h-0 flex-1 overflow-hidden rounded-box border border-hairline p-2" style={{ background: '#091015' }}>
          <Terminal onReady={setTerm} onData={onData} onResize={onResize} />
          {status === 'ended' && ended ? <EndedOverlay ended={ended} onReconnect={reconnect} /> : null}
        </div>
      )}
    </div>
  );
}

function EndedOverlay({ ended, onReconnect }: { ended: Ended; onReconnect: () => void }) {
  return (
    <div className="absolute inset-0 flex animate-fade items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]">
      <div className="card dr-surface w-full max-w-lg animate-pop dr-shadow-lg">
        <div className="card-body gap-3">
          {ended.kind === 'failed' ? (
            <ErrorAlert error={ended.error} title="Could not open a shell session" />
          ) : (
            <div role="alert" className={`alert alert-soft ${ended.kind === 'lost' ? 'alert-error' : 'alert-info'}`}>
              <div>
                <p className="font-semibold">{ended.kind === 'closed' ? 'Session ended' : ended.kind === 'lost' ? 'Connection lost' : 'Disconnected'}</p>
                <p className="break-anywhere">
                  {ended.kind === 'closed'
                    ? ended.message
                    : ended.kind === 'lost'
                      ? 'The connection to the shell closed unexpectedly.'
                      : 'You closed the connection. The shell on the server has been ended.'}
                </p>
                {ended.kind === 'closed' && ended.exitCode !== null && ended.exitCode !== undefined ? <p className="text-sm opacity-80">Exit code {ended.exitCode}</p> : null}
              </div>
            </div>
          )}
          <div className="card-actions justify-end">
            <Haptic>
              <button type="button" className="btn btn-primary btn-sm" onClick={onReconnect}>
                <RotateCw className="size-4" aria-hidden="true" /> Reconnect
              </button>
            </Haptic>
          </div>
        </div>
      </div>
    </div>
  );
}
